/**
 * WhatsNewProvider — wraps an app to auto-show the WhatsNew dialog.
 *
 * Place near the app root, inside ApiProvider and QueryClientProvider.
 * On mount, checks if cooldown has passed and fetches unseen tips.
 */

import { useMemo, type ReactNode } from 'react';
import { useApi } from '../api';
import { createWhatsNewApi } from './whatsNewApi';
import { useWhatsNew } from './useWhatsNew';
import { WhatsNewDialog } from './WhatsNewDialog';

interface WhatsNewProviderProps {
    appTarget: 'student' | 'teacher';
    /** The signed-in user's id, or undefined while logged out. Required — this
     *  provider is mounted above the router, so it must not fetch until there is
     *  someone to fetch for. See useWhatsNew's `userId`. */
    userId?: string;
    platform?: string;
    appVersion?: string;
    /** Cooldown in ms before showing again. Default 24h. */
    cooldownMs?: number;
    children: ReactNode;
}

export function WhatsNewProvider({ appTarget, userId, platform, appVersion, cooldownMs, children }: WhatsNewProviderProps) {
    const apiClient = useApi();
    const whatsNewApi = useMemo(() => createWhatsNewApi(apiClient), [apiClient]);

    const {
        isOpen,
        currentTip,
        dismiss,
        remindLater,
        complete,
        submitSurvey,
        tryNow,
        close,
        skip,
        markShown,
    } = useWhatsNew({
        api: whatsNewApi,
        appTarget,
        userId,
        platform: platform || 'web',
        appVersion,
        cooldownMs,
    });

    return (
        <>
            {children}
            <WhatsNewDialog
                isOpen={isOpen}
                onClose={close}
                currentTip={currentTip}
                onDismiss={dismiss}
                onRemindLater={remindLater}
                onTryNow={tryNow}
                onComplete={complete}
                onSubmit={submitSurvey}
                onSkip={skip}
                onShown={markShown}
                onAction={NOOP_ACTION}
            />
        </>
    );
}

// In-experience analytics events are captured in a later phase; no-op for now.
const NOOP_ACTION = () => { /* per-event capture deferred (see custom-experiences design) */ };
